import { useEffect } from "react";
import { backDepth, notifyBackDepth, onBackDepth } from "@hybrid-iptv/ui";

/**
 * Telling the APK how deep the page's own Back stack is.
 *
 * WHY THIS EXISTS (measured on a Galaxy A15, 2026-09-22). Android's Back
 * arrives in Java, and MainActivity has to choose between stepping back inside
 * the page and leaving the app. It asked `WebView.canGoBack()`. On the
 * projector that agreed with the page; on the phone it did not — with a movie
 * playing full screen and two entries behind it (`history.length` 3, `idx` 2)
 * it answered false, so the very first Back skipped leaving full screen, went
 * straight to picture-in-picture and put the app in the background. From the
 * sofa that looks exactly like "Back exited the app", which is the complaint
 * this is here to end.
 *
 * `canGoBack()` reports the WEBVIEW's navigation history. This app's history
 * is made almost entirely of `pushState` entries — every route change and
 * every layer in `backStack` — and how faithfully a WebView reflects those is
 * a per-device, per-version question we cannot answer from here and should not
 * be betting the Back button on.
 *
 * So the page stops being asked and starts telling. It reports its depth
 * whenever the depth changes; Java keeps the last number and reads it
 * synchronously when Back arrives. The page is the only side that knows what
 * is open, so it is the side that should be counting.
 *
 * The channel is a plain `@JavascriptInterface` (`window.sliqueBack`), the
 * same road the catalog's bulk channel takes and for the same reason: it costs
 * a JNI call, it does not touch Capacitor's message bridge, and it cannot
 * stall the main thread. On the website there is no such object and every call
 * here does nothing.
 */
interface BackChannel {
  /** Java keeps this number; Back reads it. */
  depth: (steps: number) => void;
  /**
   * Whether the page will ask before the app closes. Java only defers to the
   * page once it has been told yes; a build without the dialog still leaves.
   */
  confirmsExit: (will: boolean) => void;
  /** The viewer answered "Leave". Java takes it from here. */
  leave: () => void;
  /**
   * Backs sent did not land: the page is at `steps`, and `seq` is the last Back
   * it received. Java forgets the Backs it believed in flight. Missing in APKs
   * older than 2026-10-07.
   */
  settled?: (steps: number, seq: number) => void;
  /** A line for /back (scripts/dbg.mjs backstate). Missing in older APKs. */
  note?: (line: string) => void;
}

function channel(): BackChannel | undefined {
  const found = (globalThis as { sliqueBack?: BackChannel }).sliqueBack;
  return found && typeof found.depth === "function" ? found : undefined;
}

/** True in the APK, false on the website. */
export function nativeBackAvailable(): boolean {
  return channel() !== undefined;
}

/**
 * Start reporting. Returns the function to stop.
 *
 * A failed report is swallowed: if the channel ever throws, Back must still
 * work, and MainActivity falls back to its old behaviour when it has never
 * been told a depth.
 */
export function reportBackDepth(): () => void {
  const back = channel();
  if (!back) return () => undefined;
  // Only ever say something new.
  //
  // Measured on the A15 (2026-09-22): a call across this channel costs about
  // 511 µs, against 5.8 µs to read `history.state`. That is nothing once per
  // navigation — a route change costs hundreds of milliseconds — but it is
  // dear enough that repeating an answer Java already has would be careless,
  // and the shell announces on every `location.key` whether the depth moved or
  // not. Checking first is two orders of magnitude cheaper than telling.
  let told = -1;
  return onBackDepth((depth) => {
    if (depth === told) return;
    try {
      back.depth(depth);
      told = depth;
    } catch {
      // The page cannot do anything useful about a dead channel.
    }
  });
}

/**
 * Report for as long as the app is mounted, and again on every navigation.
 *
 * `backStack` announces its own pushes and every pop, but a router navigation
 * happens outside it and is invisible from there — so the shell, which does
 * see one, says so. `location` is passed rather than read here to keep this
 * module free of a router dependency.
 */
export function useBackDepthReport(location: unknown): void {
  useEffect(() => reportBackDepth(), []);
  useEffect(() => {
    // Read after the router has written its entry for this location.
    notifyBackDepth();
  }, [location]);
}

/** Tests and diagnostics: what the APK would have been told right now. */
export function currentBackDepth(): number {
  return backDepth();
}

/**
 * Install the handler Java calls when Back has nothing left to undo.
 *
 * Only one at a time — there is one dialog — and registering tells Java it may
 * stop finishing the activity on its own. Unregistering takes that back, so a
 * page that tears the dialog down cannot leave the app unclosable.
 *
 * On the website there is no channel and nothing here runs: the browser's own
 * Back at the first entry is not ours to intercept, and a page cannot honestly
 * promise to keep someone on a tab.
 */
let confirmHandler: (() => void) | null = null;

export function confirmExitHandler(onAsk: () => void): () => void {
  const back = channel();
  // No channel, no dialog. On the website "Leave" would have nothing to call
  // and the button would sit there doing nothing — and the browser's own Back
  // at the first entry was never ours to intercept in the first place.
  if (!back) return () => undefined;
  confirmHandler = onAsk;
  back.confirmsExit(true);
  return () => {
    if (confirmHandler === onAsk) confirmHandler = null;
    back.confirmsExit(false);
  };
}

/** Java's way in. Named on `window` because that is what it can reach. */
function askToLeave(): void {
  confirmHandler?.();
}

/**
 * The rail's Exit (owner, 2026-10-06: leaving took a Back for everything done
 * since Home). The same "leave?" dialog Back at the first screen opens, so one
 * press cannot close the app by mistake. Only in the app: a website has nothing
 * to leave to.
 */
export function askToLeaveApp(): void {
  if (channel()) askToLeave();
}

if (typeof window !== "undefined") {
  (window as unknown as { __sliqueConfirmExit?: () => void }).__sliqueConfirmExit = askToLeave;
  (window as unknown as { __sliqueBack?: (seq: number) => void }).__sliqueBack = nativeHistoryBack;
}

/**
 * A Back from the APK, checked until it lands.
 *
 * THE BUG (onn box, 2026-10-07). In a full-screen film, Back pressed about
 * 250 ms after another key was sent here as `history.back()` and nothing
 * happened: no popstate, `history.state` unchanged, no new depth. Java had
 * counted the Back as on its way to depth 0 and answered every later press
 * with "wait" until it gave up on it fifteen seconds later — the Back button
 * dead, full screen stuck. The same build from before the timeshift work does
 * it too.
 *
 * WHY the WebView drops the step is not settled; Chromium has several ways to
 * ignore a script's history step without a word, and the page cannot tell which
 * one it met. So the page stops trusting and checks. Every Back from Java is
 * counted until a popstate pays it off. One still owed after LAND_MS is taken
 * again, once, to the entry it was aimed at — `navigation.traverseTo` by that
 * entry's key, which cannot overshoot if the first step turns up late after
 * all: it lands on the same entry or does nothing. If that does not land
 * either, Java is told where the page really is (`settled`), so the press
 * after it works at once.
 *
 * Each check leaves a line in Java's log (`GET /back`, `dbg backstate`) with
 * what the page saw, so a device can show which way the step was lost.
 */
export const LAND_MS = 700;

interface NavigationEntryLike {
  key: string;
  index: number;
}

interface NavigationLike {
  currentEntry: NavigationEntryLike | null;
  entries: () => NavigationEntryLike[];
  traverseTo: (key: string) => { committed?: Promise<unknown>; finished?: Promise<unknown> };
  transition?: unknown;
}

/** The Navigation API, where the WebView has it (Chromium 102+). */
function navigationApi(): NavigationLike | undefined {
  const found = (globalThis as { navigation?: NavigationLike }).navigation;
  return found && typeof found.traverseTo === "function" && typeof found.entries === "function" ? found : undefined;
}

/** Backs from Java not yet paid off by a popstate. */
let owed = 0;
/** The number Java gave the last Back it sent (BackChannel.lastSent). */
let lastSeq = 0;
/** Whether the Backs owed have already been taken a second time. */
let retried = false;
let landTimer: ReturnType<typeof setTimeout> | null = null;
let watchingPops = false;

function onLandingPop(): void {
  if (owed === 0) return;
  owed -= 1;
  if (owed > 0) return;
  retried = false;
  if (landTimer !== null) clearTimeout(landTimer);
  landTimer = null;
}

function armLandCheck(): void {
  if (landTimer !== null) clearTimeout(landTimer);
  landTimer = setTimeout(checkLanded, LAND_MS);
}

/** What the page can see of its history, for the log line. */
function historyPicture(): string {
  const nav = navigationApi();
  const current = nav?.currentEntry;
  const activation = (navigator as { userActivation?: { isActive?: boolean } }).userActivation;
  return (
    `depth ${backDepth()}, history.length ${history.length}` +
    (nav ? `, entry ${current ? current.index : "?"} of ${nav.entries().length}${nav.transition ? ", a navigation under way" : ""}` : ", no Navigation API") +
    (activation && typeof activation.isActive === "boolean" ? `, activation ${activation.isActive ? "on" : "off"}` : "") +
    `, document ${document.hasFocus() ? "focused" : "not focused"}`
  );
}

function tellJava(line: string): void {
  try {
    channel()?.note?.(line);
  } catch {
    // Diagnostics only.
  }
}

function checkLanded(): void {
  landTimer = null;
  if (owed === 0) return;
  const missing = owed;
  if (!retried) {
    const nav = navigationApi();
    const current = nav?.currentEntry;
    const target = nav && current && current.index >= missing ? nav.entries()[current.index - missing] : undefined;
    if (nav && target) {
      tellJava(`Back #${lastSeq}: ${missing} step(s) not landed after ${LAND_MS} ms (${historyPicture()}); taking it again`);
      retried = true;
      // One traversal, so one popstate pays the whole debt.
      owed = 1;
      armLandCheck();
      try {
        const result = nav.traverseTo(target.key);
        // A traversal the browser turns down rejects; the check above covers it.
        result.committed?.catch(() => undefined);
        result.finished?.catch(() => undefined);
        return;
      } catch {
        if (landTimer !== null) clearTimeout(landTimer);
        landTimer = null;
      }
    }
  }
  tellJava(`Back #${lastSeq}: ${missing} step(s) not landed${retried ? " after a second try" : ""} (${historyPicture()}); settling`);
  owed = 0;
  retried = false;
  try {
    channel()?.settled?.(backDepth(), lastSeq);
  } catch {
    // Java falls back to forgetting the Back after BackChannel.STALE_MS.
  }
}

/**
 * Java's Back: one `history.back()`, watched until it lands (see above).
 * `seq` is Java's number for it, given back if the page has to settle.
 */
export function nativeHistoryBack(seq: number): void {
  lastSeq = seq;
  // A new press earns its own second try.
  retried = false;
  if (!watchingPops && typeof window !== "undefined") {
    window.addEventListener("popstate", onLandingPop);
    watchingPops = true;
  }
  owed += 1;
  armLandCheck();
  history.back();
}

/** Tests only: forget Backs owed without touching history. */
export function resetNativeBack(): void {
  if (landTimer !== null) clearTimeout(landTimer);
  landTimer = null;
  owed = 0;
  lastSeq = 0;
  retried = false;
}

/**
 * The viewer said Leave.
 *
 * What happens next is native's call, not the page's: with something playing
 * this is the one automatic picture-in-picture the owner kept ("pip isn't
 * automatic unless leaving app from root"), and with nothing playing the app
 * closes. Either way the page does not decide.
 */
export function leaveApp(): void {
  channel()?.leave();
}
